// installPrompt.ts — captures Chrome/Android's `beforeinstallprompt` event so any
// part of the UI can offer a one-tap "add to home screen" LATER.
//
// The event fires once, early, and only if the page is installable (valid manifest
// + a registered service worker — see public/sw.js + main.tsx). It can't be re-fired,
// so we stash the deferred event here and let consumers (the Today tile AND the
// Settings row) read it or trigger it on demand. Importing this module installs the
// listeners; do it once, early (main.tsx).
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();
const notify = () => subscribers.forEach((f) => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // suppress Chrome's mini-infobar; we drive install from our own UI
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** Has Chrome offered an installable prompt we can fire? */
export const installPromptAvailable = (): boolean => deferred !== null;

/** Re-render hook: call the returned fn to unsubscribe. */
export function onInstallPromptChange(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/** Fire the one-tap install. Returns the outcome, or 'unavailable' if Chrome hasn't offered it. */
export async function triggerInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  try {
    await deferred.prompt();
    const choice = await deferred.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
    return choice.outcome;
  } finally {
    deferred = null; // a prompt can only be used once
    notify();
  }
}
